import { Button } from '@my/ui';
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { EquipmentOption } from '../lib/equipment';
import {
  hasEmail,
  hasEquipment,
  hasMobile,
  REFERENCE_LABEL,
  type ImportResourceType,
  type ResourceImportDraft,
} from '../lib/types';
import { isCommittable, validateAll, type RowIssues } from '../lib/validate';

const PAGE_SIZE = 50;

interface PreviewStepProps {
  drafts: ResourceImportDraft[];
  type: ImportResourceType;
  equipmentOptions: EquipmentOption[];
  excludedRefs: Set<string>;
  onDraftChange: (rowRef: string, patch: Partial<ResourceImportDraft>) => void;
  onToggleExclude: (rowRef: string) => void;
  onSetAllExcluded: (excluded: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
}

/**
 * Step 4 — review and fix before committing. Every cell is editable, rows can be
 * excluded, and each row carries an error / warning badge. Rows with blocking
 * errors can't be imported; warnings (unmatched equipment type, duplicate name)
 * import as-is.
 */
export function PreviewStep({
  drafts,
  type,
  equipmentOptions,
  excludedRefs,
  onDraftChange,
  onToggleExclude,
  onSetAllExcluded,
  onBack,
  onContinue,
}: PreviewStepProps) {
  const [page, setPage] = useState(0);

  const issuesByRef = useMemo(() => validateAll(drafts), [drafts]);

  const counts = useMemo(() => {
    let ready = 0;
    let blocked = 0;
    let excluded = 0;
    for (const d of drafts) {
      if (excludedRefs.has(d.rowRef)) {
        excluded += 1;
        continue;
      }
      if (isCommittable(issuesByRef.get(d.rowRef))) ready += 1;
      else blocked += 1;
    }
    return { ready, blocked, excluded, total: drafts.length };
  }, [drafts, excludedRefs, issuesByRef]);

  const pageCount = Math.max(1, Math.ceil(drafts.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageDrafts = drafts.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const allIncluded = excludedRefs.size === 0;

  const showEquipment = hasEquipment(type);
  const showMobile = hasMobile(type);
  const showEmail = hasEmail(type);

  return (
    <div className="space-y-4">
      {/* Counts + bulk include */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CircleCheck className="h-4 w-4" /> {counts.ready} ready
          </span>
          {counts.blocked > 0 && (
            <span className="inline-flex items-center gap-1 text-red-600">
              <CircleX className="h-4 w-4" /> {counts.blocked} blocked
            </span>
          )}
          {counts.excluded > 0 && (
            <span className="text-slate-500">{counts.excluded} excluded</span>
          )}
          <span className="text-slate-400">of {counts.total}</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={allIncluded}
            onChange={(e) => onSetAllExcluded(!e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300"
          />
          Include all
        </label>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full min-w-[48rem] border-collapse text-xs">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <Th className="w-8" />
              <Th className="w-8" />
              <Th>Name *</Th>
              <Th>Display name</Th>
              <Th>{REFERENCE_LABEL[type]}</Th>
              {showEquipment && <Th>Equipment type</Th>}
              {showMobile && <Th>Mobile</Th>}
              {showEmail && <Th>Email</Th>}
            </tr>
          </thead>
          <tbody>
            {pageDrafts.map((d) => {
              const issues = issuesByRef.get(d.rowRef) ?? { errors: [], warnings: [] };
              const excluded = excludedRefs.has(d.rowRef);
              return (
                <tr
                  key={d.rowRef}
                  className={
                    'border-t border-slate-100 ' +
                    (excluded ? 'opacity-40' : issues.errors.length ? 'bg-red-50/40' : '')
                  }
                >
                  <Td>
                    <input
                      type="checkbox"
                      checked={!excluded}
                      onChange={() => onToggleExclude(d.rowRef)}
                      className="h-3.5 w-3.5 rounded border-slate-300"
                    />
                  </Td>
                  <Td>
                    <IssueBadge issues={issues} />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.name}
                      invalid={!d.name.trim()}
                      onChange={(v) => onDraftChange(d.rowRef, { name: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.displayName}
                      placeholder="(uses Name)"
                      onChange={(v) => onDraftChange(d.rowRef, { displayName: v })}
                    />
                  </Td>
                  <Td>
                    <EditCell
                      value={d.reference}
                      onChange={(v) => onDraftChange(d.rowRef, { reference: v })}
                    />
                  </Td>
                  {showEquipment && (
                    <Td>
                      <EquipmentCell
                        draft={d}
                        options={equipmentOptions}
                        onChange={(patch) => onDraftChange(d.rowRef, patch)}
                      />
                    </Td>
                  )}
                  {showMobile && (
                    <Td>
                      <EditCell
                        value={d.mobileNumber}
                        onChange={(v) => onDraftChange(d.rowRef, { mobileNumber: v })}
                      />
                    </Td>
                  )}
                  {showEmail && (
                    <Td>
                      <EditCell
                        value={d.email}
                        onChange={(v) => onDraftChange(d.rowRef, { email: v })}
                      />
                    </Td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-2 text-xs">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </Button>
          <span className="text-slate-500">
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {counts.ready === 0 && (
            <span className="text-xs text-amber-600">No rows are ready to import.</span>
          )}
          <Button type="button" onClick={onContinue} disabled={counts.ready === 0}>
            Continue ({counts.ready})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`whitespace-nowrap px-2 py-1.5 font-semibold ${className}`}>{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-2 py-1 align-middle">{children}</td>;
}

function IssueBadge({ issues }: { issues: RowIssues }) {
  if (issues.errors.length) {
    return (
      <span title={issues.errors.join('\n')}>
        <CircleX className="h-4 w-4 text-red-500" />
      </span>
    );
  }
  if (issues.warnings.length) {
    return (
      <span title={issues.warnings.join('\n')}>
        <TriangleAlert className="h-4 w-4 text-amber-500" />
      </span>
    );
  }
  return (
    <span title="Ready">
      <CircleCheck className="h-4 w-4 text-emerald-500" />
    </span>
  );
}

function EditCell({
  value,
  onChange,
  placeholder,
  invalid,
  width = 'w-full',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  width?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={
        `${width} min-w-[4rem] rounded-sm border bg-white px-1.5 py-0.5 text-xs ` +
        (invalid ? 'border-red-300 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-400') +
        ' focus:outline-none focus:ring-1'
      }
    />
  );
}

/**
 * Equipment type cell — a picker over the org's types for this class. Unmatched
 * file text is shown as a distinct "no match" option so the operator can see
 * what the file said and pick the right type without leaving the grid.
 */
function EquipmentCell({
  draft,
  options,
  onChange,
}: {
  draft: ResourceImportDraft;
  options: EquipmentOption[];
  onChange: (patch: Partial<ResourceImportDraft>) => void;
}) {
  const unmatched = !!draft.equipmentTypeText.trim() && !draft.organisationEquipmentTypeId;
  return (
    <select
      value={draft.organisationEquipmentTypeId}
      onChange={(e) =>
        onChange({
          organisationEquipmentTypeId: e.target.value,
          // Keep the resolver honest: once the operator picks manually, the raw
          // text no longer drives the id.
          equipmentTypeText: e.target.value
            ? (options.find((o) => o.id === e.target.value)?.label ?? draft.equipmentTypeText)
            : draft.equipmentTypeText,
        })
      }
      title={
        unmatched
          ? `File said "${draft.equipmentTypeText.trim()}" — no matching equipment type`
          : undefined
      }
      className={
        'w-full min-w-[8rem] rounded-sm border bg-white px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 ' +
        (unmatched
          ? 'border-amber-300 text-amber-700 focus:ring-amber-400'
          : 'border-slate-200 focus:ring-blue-400')
      }
    >
      <option value="">
        {unmatched ? `? ${draft.equipmentTypeText.trim()} — no match` : '— none —'}
      </option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
